import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Loader2, Plus, Trash2 } from "lucide-react";

import {
  type CostParameterWrite,
  parametersApi,
} from "@/api/parameters";
import { useCanEdit } from "@/hooks/useRole";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

interface Row {
  code: string;
  name: string;
  value: string;
  unit: string;
  quantity: string;
  notes: string;
}

const SUGGESTED: { code: string; name: string; unit: string }[] = [
  { code: "gas_price", name: "Ціна газу", unit: "грн/м³" },
  { code: "rent", name: "Оренда", unit: "грн/місяць" },
  { code: "service", name: "Обслуговування", unit: "грн/місяць" },
];

export function DataPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canEdit = useCanEdit();

  const [month, setMonth] = useState(currentMonth());

  const items = useQuery({
    queryKey: ["cost-parameters", month],
    queryFn: () => parametersApi.getCostParameters(month),
  });

  const [rows, setRows] = useState<Row[]>([]);

  // Sync local state when fetched.
  useEffect(() => {
    if (items.data) {
      if (items.data.length > 0) {
        setRows(
          items.data.map((it) => ({
            code: it.code,
            name: it.name,
            value: String(it.value),
            unit: it.unit,
            quantity: String(it.quantity),
            notes: it.notes ?? "",
          })),
        );
      } else {
        // Pre-seed з типовими статтями для зручності першого вводу.
        setRows(
          SUGGESTED.map((s) => ({
            ...s,
            value: "",
            quantity: "1",
            notes: "",
          })),
        );
      }
    }
  }, [items.data]);

  const save = useMutation({
    mutationFn: () => {
      const payload: CostParameterWrite[] = rows
        .filter((r) => r.code && r.name && r.value !== "")
        .map((r) => ({
          code: r.code,
          name: r.name,
          value: Number(r.value),
          unit: r.unit,
          quantity: Number(r.quantity || "1"),
          notes: r.notes || null,
        }));
      return parametersApi.putCostParameters(month, payload);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cost-parameters", month] }),
  });

  const copyPrev = useMutation({
    mutationFn: () => parametersApi.copyFromPreviousMonth(month),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cost-parameters", month] }),
  });

  const setRow = (i: number, patch: Partial<Row>) =>
    setRows((s) => s.map((r, j) => (i === j ? { ...r, ...patch } : r)));

  const codesUnique =
    new Set(rows.map((r) => r.code).filter(Boolean)).size === rows.filter((r) => r.code).length;
  const valid = rows.length > 0 && rows.every((r) => r.code && r.name && r.value !== "" && r.unit) && codesUnique;

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl font-semibold text-brand-navy-700">{t("data_page.title")}</h1>
        <p className="text-sm text-slate-500">{t("data_page.subtitle")}</p>
        {!canEdit && <div className="mt-2 text-xs text-amber-600">{t("common.read_only")}</div>}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-700">{t("data_page.month")}:</label>
              <Input type="month" value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-44" />
            </div>
            {canEdit && (
              <Button variant="outline" size="sm"
                onClick={() => {
                  if (window.confirm(t("data_page.copy_from_previous_confirm"))) {
                    copyPrev.mutate();
                  }
                }}
                disabled={copyPrev.isPending}>
                <Copy className="h-4 w-4" /> {t("data_page.copy_from_previous")}
              </Button>
            )}
          </div>
          <CardDescription>{t("data_page.common_items_hint")}</CardDescription>
        </CardHeader>
        <CardContent>
          {items.isLoading ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="p-2 w-[20%]">{t("data_page.code")}</th>
                    <th className="p-2 w-[25%]">{t("data_page.name")}</th>
                    <th className="p-2 w-[15%]">{t("data_page.value")}</th>
                    <th className="p-2 w-[15%]">{t("data_page.unit")}</th>
                    <th className="p-2 w-[10%]">{t("data_page.quantity")}</th>
                    <th className="p-2">{t("data_page.notes")}</th>
                    {canEdit && <th className="p-2 w-12" />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100 align-top">
                      <td className="p-2">
                        <Input value={r.code} disabled={!canEdit}
                          placeholder="gas_price"
                          onChange={(e) => setRow(i, { code: e.target.value.toLowerCase() })} />
                      </td>
                      <td className="p-2">
                        <Input value={r.name} disabled={!canEdit}
                          onChange={(e) => setRow(i, { name: e.target.value })} />
                      </td>
                      <td className="p-2">
                        <Input type="number" step="0.0001" value={r.value} disabled={!canEdit}
                          onChange={(e) => setRow(i, { value: e.target.value })} />
                      </td>
                      <td className="p-2">
                        <Input value={r.unit} disabled={!canEdit}
                          onChange={(e) => setRow(i, { unit: e.target.value })} />
                      </td>
                      <td className="p-2">
                        <Input type="number" step="0.01" value={r.quantity} disabled={!canEdit}
                          onChange={(e) => setRow(i, { quantity: e.target.value })} />
                      </td>
                      <td className="p-2">
                        <Input value={r.notes} disabled={!canEdit}
                          onChange={(e) => setRow(i, { notes: e.target.value })} />
                      </td>
                      {canEdit && (
                        <td className="p-2">
                          <Button variant="ghost" size="icon"
                            onClick={() => setRows((s) => s.filter((_, j) => j !== i))}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>

              {rows.length === 0 && (
                <div className="text-sm text-slate-500 py-4">{t("data_page.no_items")}</div>
              )}
            </div>
          )}

          {canEdit && (
            <div className="flex items-center gap-3 mt-4">
              <Button variant="outline"
                onClick={() => setRows((s) => [...s, { code: "", name: "", value: "", unit: "", quantity: "1", notes: "" }])}>
                <Plus className="h-4 w-4" /> {t("data_page.add_row")}
              </Button>
              <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>
                {save.isPending ? t("common.saving") : t("common.save")}
              </Button>
              {save.isSuccess && <span className="text-sm text-brand-mint-600">{t("common.saved")}</span>}
              {save.isError && <span className="text-sm text-red-600">{t("common.error")}</span>}
              {copyPrev.isError && <span className="text-sm text-red-600">{t("common.error")}</span>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
